import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from "react";
import { isPWA } from "../Utils/browser";
import { useUserContext } from "./UserContext";
import { appInsights } from "../AppInsights";
import { EventName_UserInteraction, EventProperty_Value_ApplicationType, EventProperty_Value_InstallPWA, UserInteractionEventProperties } from "../store/models/customEvents";

// How to use PromptToInstallContext
// 1. Wrap your app with <PromptToInstallProvider /> e.g. in App.tsx
// 2. Use usePromptToInstall() hook to get the deferredEvt and hidePrompt function
// 3. Call prompt() function on deferredEvt to show the install prompt

export interface IBeforeInstallPromptEvent extends Event {
    readonly platforms: string[];
    readonly userChoice: Promise<{
        outcome: 'accepted' | 'dismissed';
        platform: string;
    }>;
    prompt(): Promise<void>;
}

export type PromptCtx = {
    state: 'not-supported' | 'supported' | 'installed';
    deferredEvt: IBeforeInstallPromptEvent | null;
    hidePrompt?: () => void;
};

const PromptToInstall = createContext<PromptCtx>({ state: 'not-supported', deferredEvt: null });

export function PromptToInstallProvider({ children }: { children: ReactNode }) {
    const [deferredEvt, setDeferredEvt] = useState<IBeforeInstallPromptEvent | null>(null);
    const [state, setState] = useState<'not-supported' | 'supported' | 'installed'>('not-supported');
    const { userProfile } = useUserContext();

    const hidePrompt = useCallback(() => {
        setDeferredEvt(null);
    }, []);

    useEffect(() => {
        switch (true) {
            // Not supported: for example, Firefox, Safari, etc.
            case !('onbeforeinstallprompt' in window):
                setState('not-supported');
                break;
            // Installed: already installed and running as PWA or running in browser with PWA installed (in this case deferredEvt is null)
            case isPWA() || !deferredEvt:
                setState('installed');
                break;
            // Supported and not installed
            default:
                setState('supported');
                break;
        }

        const ready = (e: IBeforeInstallPromptEvent) => {
            setDeferredEvt(e);
            e.preventDefault();
        };

        const installed = () => {
            const properties: UserInteractionEventProperties = {
                applicationType: EventProperty_Value_ApplicationType,
                userId: userProfile?.username,
                interactionType: EventProperty_Value_InstallPWA
            };

            appInsights.trackEvent({ name: EventName_UserInteraction, properties });
        };

        window.addEventListener('beforeinstallprompt', ready as any);
        window.addEventListener('appinstalled', installed as any);

        return () => {
            window.removeEventListener('beforeinstallprompt', ready as any);
            window.removeEventListener('appinstalled', installed as any);
        };
    }, [deferredEvt]);

    return (
        <PromptToInstall.Provider value={{ state, deferredEvt, hidePrompt }}>
            {children}
        </PromptToInstall.Provider>
    );
}

export function usePromptToInstall() {
    const ctx = useContext(PromptToInstall);
    if (!ctx) {
        throw new Error('Cannot use usePromptToInstall() outside <PromptToInstallProvider />');
    }
    return ctx;
}